/**
 * Formateo del front.
 *
 * Los formateadores de valor viven en `@ops/contracts/format`: `narrativa.ts`
 * arma el titular con los mismos números que muestran las tarjetas, así que una
 * segunda implementación acá haría que el titular y la tarjeta puedan decir
 * cifras distintas. Este archivo **re-exporta** y agrega solo lo que es de
 * presentación pura y no existe en el contrato.
 */
import {
  MENOS,
  SIN_DATO,
  formatDelta,
  formatMetricValue,
  formatPeriodo,
  formatPeriodoCorto,
  formatRelativeDays,
  listaEs,
  periodoAnterior,
  type DeltaBase,
  type Unit,
} from '@ops/contracts'

export {
  MENOS,
  SIN_DATO,
  formatDelta,
  formatMetricValue,
  formatPeriodo,
  formatPeriodoCorto,
  formatRelativeDays,
  listaEs,
  periodoAnterior,
}

/**
 * dd/mm a partir de una fecha ISO, sin pasar por Date: `new Date('2026-08-15')`
 * se interpreta como medianoche UTC y en Buenos Aires mostraría el 14.
 */
export function formatFechaCorta(iso: string | null): string | null {
  if (iso === null) return null
  const partes = iso.slice(0, 10).split('-')
  const dia = partes[2]
  const mes = partes[1]
  if (dia === undefined || mes === undefined) return iso
  return `${dia}/${mes}`
}

/**
 * Texto de la base de un delta, que la regla de números derivables obliga a
 * publicar al lado del porcentaje: `contra junio 2026 ($61.000)`.
 *
 * Devuelve null cuando no hay base: sin base no se publica el porcentaje.
 */
export function formatBaseDelta(
  base: DeltaBase | null | undefined,
  unidad: Unit,
): string | null {
  if (base === null || base === undefined) return null
  const periodo = formatPeriodo(base.periodo)
  if (base.valor === null) return `contra ${periodo} (sin dato)`
  return `contra ${periodo} (${formatMetricValue(base.valor, unidad)})`
}

/**
 * Dirección de un delta, para elegir icono y tono. `direccion` dice si subir es
 * bueno; sin ella el delta se muestra en neutro, porque un número solo no sabe
 * si crecer está bien (MRR) o mal (churn).
 */
export type Tendencia = 'sube' | 'baja' | 'plano' | 'sin-dato'

export function tendenciaDe(deltaPct: number | null | undefined): Tendencia {
  if (deltaPct === null || deltaPct === undefined) return 'sin-dato'
  if (deltaPct > 0) return 'sube'
  if (deltaPct < 0) return 'baja'
  return 'plano'
}

/** Iniciales para el avatar. Dos como máximo; '?' cuando no hay nombre. */
export function iniciales(nombre: string | null | undefined): string {
  if (nombre === null || nombre === undefined) return '?'
  const partes = nombre
    .trim()
    .split(/\s+/)
    .filter((parte) => parte.length > 0)
    .slice(0, 2)
  if (partes.length === 0) return '?'
  return partes.map((parte) => parte.charAt(0).toUpperCase()).join('')
}

/** Pluralización simple en español: `plural(1,'frente','frentes')`. */
export function plural(n: number, singular: string, plural_: string): string {
  return n === 1 ? singular : plural_
}
